import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const ProfilePage = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('stats');
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editForm, setEditForm] = useState({ bio: '' });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const fetchProfile = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [profileRes, statsRes, tradesRes, crimesRes, memberRes] = await Promise.all([
        supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single(),
        supabase?.from('player_stats')?.select('*')?.eq('user_id', user?.id)?.single(),
        supabase?.from('trade_history')?.select('id, order_type, quantity, price, created_at, stocks(ticker)')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(5),
        supabase?.from('crime_attempts')?.select('id, success, reward, created_at, crime_definitions(name)')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(5),
        supabase?.from('faction_members')?.select('rank, factions(faction_name)')?.eq('user_id', user?.id)?.maybeSingle(),
      ]);

      if (profileRes?.error && profileRes?.error?.code !== 'PGRST116') throw profileRes?.error;
      if (statsRes?.error && statsRes?.error?.code !== 'PGRST116') throw statsRes?.error;

      setProfile(profileRes?.data || null);
      setPlayerStats(statsRes?.data || null);

      // Build activity feed from trades + crimes
      const activities = [];
      (tradesRes?.data || [])?.forEach(t => {
        activities?.push({
          id: `trade-${t?.id}`,
          type: 'trade',
          desc: `${t?.order_type === 'buy' ? 'Bought' : 'Sold'} ${t?.quantity} ${t?.stocks?.ticker || 'stock'} @ $${parseFloat(t?.price || 0)?.toFixed(2)}`,
          time: new Date(t?.created_at)?.toLocaleDateString(),
          color: 'text-success',
        });
      });
      (crimesRes?.data || [])?.forEach(c => {
        activities?.push({
          id: `crime-${c?.id}`,
          type: 'crime',
          desc: `${c?.crime_definitions?.name || 'Crime'} — ${c?.success ? 'Success' : 'Failed'}`,
          time: new Date(c?.created_at)?.toLocaleDateString(),
          color: c?.success ? 'text-warning' : 'text-error',
        });
      });
      activities?.sort((a, b) => b?.id?.localeCompare(a?.id));
      setRecentActivity(activities?.slice(0, 8));

      const factionData = memberRes?.data;
      if (factionData) {
        setProfile(prev => ({
          ...prev,
          faction: factionData?.factions?.faction_name || null,
          factionRank: factionData?.rank || null,
        }));
      }

      setEditForm({ bio: profileRes?.data?.bio || '' });
    } catch (err) {
      console.error('Profile fetch error:', err);
      setError(err?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSaveEdit = async () => {
    if (!user?.id) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ bio: editForm?.bio, updated_at: new Date()?.toISOString() })?.eq('id', user?.id);
      if (error) throw error;
      setProfile(prev => ({ ...prev, bio: editForm?.bio }));
      setSaveMsg('Profile updated!');
      setIsEditing(false);
    } catch (err) {
      setSaveMsg(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'stats', label: 'Stats', icon: 'BarChart2' },
    { id: 'skills', label: 'Skills', icon: 'Zap' },
    { id: 'history', label: 'History', icon: 'Clock' },
  ];

  const SkillBar = ({ label, value, max = 100, color = 'bg-primary' }) => (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">{parseFloat(value || 0)?.toFixed(1)}</span>
      </div>
      <div className="w-full h-2 bg-surface rounded-full">
        <div className={`h-2 rounded-full ${color} transition-all`} style={{ width: `${Math.min(100, (parseFloat(value || 0) / max) * 100)}%` }} />
      </div>
    </div>
  );

  const username = profile?.username || user?.email?.split('@')?.[0] || 'Player';
  const level = playerStats?.level || 1;
  const xp = parseFloat(playerStats?.xp || 0);
  const xpToNext = parseFloat(playerStats?.xp_to_next_level || 10000);
  const netWorth = parseFloat(playerStats?.net_worth || 0);
  const cash = parseFloat(playerStats?.cash || 0);
  const tradingSkill = parseFloat(playerStats?.trading_skill || 0);
  const businessSkill = parseFloat(playerStats?.business_skill || 0);
  const criminalSkill = parseFloat(playerStats?.criminal_skill || 0);
  const reputation = parseFloat(playerStats?.reputation || 0);

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(p => !p)}
        userStats={{
          username,
          level,
          energy: playerStats?.energy || 0,
          maxEnergy: playerStats?.max_energy || 100,
          xp,
          xpToNext,
        }}
      />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-4xl mx-auto">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/30 flex items-center gap-2">
              <Icon name="AlertCircle" size={14} color="var(--color-error)" />
              <span className="text-sm text-error">{error}</span>
              <button onClick={fetchProfile} className="ml-auto text-xs text-primary underline">Retry</button>
            </div>
          )}

          {loading ? (
            <div className="space-y-4">
              <div className="card p-6 animate-pulse">
                <div className="flex items-center gap-5">
                  <div className="w-20 h-20 rounded-full bg-primary/10" />
                  <div className="space-y-2 flex-1">
                    <div className="h-6 w-40 rounded bg-primary/10" />
                    <div className="h-4 w-64 rounded bg-primary/10" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Profile Header */}
              <div className="card p-6 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-5">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full bg-primary/10 border-2 border-primary/40 flex items-center justify-center">
                        <span className="text-3xl font-bold text-primary">{username?.[0]?.toUpperCase()}</span>
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-success border-2 border-background" />
                    </div>
                    <div>
                      <h1 className="text-h2 font-heading text-foreground">{username}</h1>
                      {isEditing ? (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="text"
                            value={editForm?.bio}
                            onChange={e => setEditForm(prev => ({ ...prev, bio: e?.target?.value }))}
                            placeholder="Add a bio..."
                            maxLength={160}
                            className="text-sm bg-surface border border-border rounded px-2 py-1 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 w-64"
                          />
                          <button onClick={handleSaveEdit} disabled={saving} className="text-xs px-2 py-1 bg-primary text-zinc-950 rounded font-semibold disabled:opacity-50">
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button onClick={() => setIsEditing(false)} className="text-xs px-2 py-1 border border-border rounded text-muted-foreground">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{profile?.bio || 'No bio set.'}</p>
                      )}
                      {saveMsg && <p className="text-xs text-success mt-1">{saveMsg}</p>}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">Lv.{level}</span>
                        {profile?.faction && (
                          <span className="text-xs text-muted-foreground">{profile?.faction} · {profile?.factionRank}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    aria-label="Edit profile"
                    className="flex items-center gap-2 px-3 py-1.5 bg-surface border border-border rounded-md text-sm text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all"
                  >
                    <Icon name="Edit" size={14} />
                    Edit
                  </button>
                </div>

                {/* XP Bar */}
                <div className="mt-5">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">XP Progress</span>
                    <span className="font-mono text-foreground">{xp?.toLocaleString()} / {xpToNext?.toLocaleString()}</span>
                  </div>
                  <div className="w-full h-2 bg-surface rounded-full">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-primary to-cyan-400"
                      style={{ width: `${xpToNext > 0 ? Math.min(100, (xp / xpToNext) * 100) : 0}%` }}
                    />
                  </div>
                </div>

                {/* Key Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-4 border-t border-border">
                  {[
                    { label: 'Net Worth', value: netWorth >= 1000000 ? `$${(netWorth / 1000000)?.toFixed(2)}M` : `$${netWorth?.toLocaleString()}`, color: 'text-success' },
                    { label: 'Cash', value: cash >= 1000 ? `$${(cash / 1000)?.toFixed(0)}K` : `$${cash?.toLocaleString()}`, color: 'text-foreground' },
                    { label: 'Level', value: level, color: 'text-primary' },
                    { label: 'Reputation', value: Math.round(reputation), color: 'text-warning' },
                  ]?.map((stat) => (
                    <div key={stat?.label} className="text-center">
                      <p className={`text-lg font-mono font-bold ${stat?.color}`}>{stat?.value}</p>
                      <p className="text-xs text-muted-foreground">{stat?.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mb-4 bg-surface rounded-lg p-1 w-fit">
                {tabs?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      activeTab === tab?.id ? 'bg-primary text-zinc-950' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={14} />
                    {tab?.label}
                  </button>
                ))}
              </div>

              {activeTab === 'stats' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="card p-5">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Trading Stats</h3>
                    <div className="space-y-3">
                      {[
                        { label: 'Total Trades', value: playerStats?.total_trades || 0 },
                        { label: 'Trading Skill', value: tradingSkill?.toFixed(1) },
                        { label: 'Net Worth', value: `$${netWorth?.toLocaleString()}` },
                        { label: 'Cash Available', value: `$${cash?.toLocaleString()}` },
                      ]?.map((stat) => (
                        <div key={stat?.label} className="flex justify-between">
                          <span className="text-sm text-muted-foreground">{stat?.label}</span>
                          <span className="text-sm font-mono text-foreground">{stat?.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="card p-5">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Activity Stats</h3>
                    <div className="space-y-3">
                      {[
                        { label: 'Total Crimes', value: playerStats?.total_crimes || 0 },
                        { label: 'Criminal Skill', value: criminalSkill?.toFixed(1) },
                        { label: 'Business Skill', value: businessSkill?.toFixed(1) },
                        { label: 'Joined', value: profile?.created_at ? new Date(profile?.created_at)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A' },
                      ]?.map((stat) => (
                        <div key={stat?.label} className="flex justify-between">
                          <span className="text-sm text-muted-foreground">{stat?.label}</span>
                          <span className="text-sm font-mono text-foreground">{stat?.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'skills' && (
                <div className="card p-5">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-5">Skill Progression</h3>
                  <SkillBar label="Trading Skill" value={tradingSkill} max={100} color="bg-primary" />
                  <SkillBar label="Business Skill" value={businessSkill} max={100} color="bg-success" />
                  <SkillBar label="Criminal Skill" value={criminalSkill} max={100} color="bg-red-500" />
                  <SkillBar label="Reputation" value={reputation} max={100} color="bg-warning" />
                  <p className="text-xs text-muted-foreground mt-4">
                    Skills improve through repeated successful actions in each category.
                  </p>
                </div>
              )}

              {activeTab === 'history' && (
                <div className="card p-5">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Recent Activity</h3>
                  {recentActivity?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No recent activity found.</p>
                  ) : (
                    <div className="space-y-3">
                      {recentActivity?.map((item) => (
                        <div key={item?.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                          <span className={`text-sm ${item?.color}`}>{item?.desc}</span>
                          <span className="text-xs text-muted-foreground">{item?.time}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
